import { normalizeAnswer } from './questionUtils';

/**
 * Le choix d'une réponse IMPORTÉE de l'export du ministère (évaluations nationales, 10/10/2026).
 *
 * L'export appauvrit l'écriture des choix : il perd les exposants mis en forme (« a³ » y devient « a3 ») et
 * abrège les longs choix avec des points de suspension (« le nuage... est d'une taille exceptionnelle »). La
 * base garde l'écriture du sujet ; la réponse importée retrouve son choix dans cet ordre :
 *   0. la réponse telle quelle, au premier choix égal (la comparaison d'avant) ;
 *   1. sinon, normalisée (normalizeAnswer : casse, accents, apostrophes, espaces de milliers) ;
 *   2. sinon, un exposant vaut le chiffre (² = 2, ³ = 3) ;
 *   3. sinon, « … » ou « ... » dans la réponse vaut n'importe quel texte (motif ancré au début et à la fin).
 * Aux temps 1 à 3, un seul choix doit correspondre : s'il y en a plusieurs, la réponse n'en reçoit aucun,
 * jamais le mauvais. Mesuré sur les 42 exports réels de 2026 : aucune collision d'exposant, aucun motif ambigu.
 * Réservé aux réponses importées : ni les exerciseurs ni le quiz élève ne passent par ici. La MÊME règle, dans le
 * même ordre, vit au back pour le calcul des groupes de besoin (clustering.py, correspondance_choix) : front et
 * back donnent toujours le même choix.
 *
 * @param {string} reponse - la réponse telle qu'importée
 * @param {Array<{type?: string, contenu?: string, image?: string}>} choix - les choix de la question
 * @returns {number} l'indice du choix, -1 si aucun
 */
export const choixDeLaReponseImportee = (reponse, choix = []) => {
    const brute = String(reponse ?? '');
    if (!brute.trim()) return -1;
    // Un choix image se reconnaît à son contenu (« Image HD ») ou à son URL, comme avant.
    const valeursBrutes = choix.map((c) => (c?.type === 'image' ? [c.contenu, c.image] : [c?.contenu])
        .filter((v) => v != null && v !== '').map(String));

    const telleQuelle = valeursBrutes.findIndex((vs) => vs.includes(brute));
    if (telleQuelle !== -1) return telleQuelle;

    const r = normalizeAnswer(brute);
    const valeurs = valeursBrutes.map((vs) => vs.map((v) => normalizeAnswer(v)));
    const indicesQui = (test) => valeurs.reduce((acc, vs, i) => (vs.some(test) ? [...acc, i] : acc), []);
    const seulOuAucun = (indices) => (indices.length === 1 ? indices[0] : -1);

    const normalisees = indicesQui((v) => v === r);
    if (normalisees.length) return seulOuAucun(normalisees);

    const sansExposant = (s) => s.replace(/²/g, '2').replace(/³/g, '3');
    const parExposant = indicesQui((v) => sansExposant(v) === sansExposant(r));
    if (parExposant.length) return seulOuAucun(parExposant);

    const POINTS = /…|\.\.\./;
    if (!POINTS.test(r)) return -1;
    const echapper = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const motif = new RegExp(`^${r.split(/…|\.\.\./).map(echapper).join('.*')}$`);
    return seulOuAucun(indicesQui((v) => motif.test(v)));
};
